/** How far a pointer may move between its pointerdown and pointerup and still be a tap. */
export const DRAG_THRESHOLD = 10

/** How long after a pointerup acted a pointer click is still that press's own click. */
export const CLICK_AFTER_POINTERUP_MS = 1000

/**
 * How long after mounting a pointer click with no press of the button behind it is taken
 * for the tap that navigated here, landing on the button that replaced the one it pressed.
 */
export const GHOST_CLICK_MS = 600

/** What a pointerup is compared against: the pointer that went down on the button. */
export interface PressPoint {
  pointerId: number
  clientX: number
  clientY: number
}

/**
 * Which of a CTA's pointerups and pointer clicks act, so each press acts once.
 *
 * The pointerup acts first: it survives the page shifting under the finger, which sends
 * the browser's click to whatever slid there. A pointer click acts only for a press whose
 * pointerup did not: iOS can deliver a tap on the button as the click alone, the press's
 * pointer events cancelled or never completing there.
 */
export function createPressActivation(now: () => number = () => performance.now()) {
  let mountedAt = -Infinity
  let pressedAt: number | null = null
  let down: PressPoint | null = null
  let pointerupActedAt: number | null = null

  return {
    mounted(): void {
      mountedAt = now()
    },

    pointerdown(point: PressPoint): void {
      down = { pointerId: point.pointerId, clientX: point.clientX, clientY: point.clientY }
      pressedAt = now()
      pointerupActedAt = null
    },

    /** A touch began on the button: the only sign of a press where there are no pointer events. */
    touchstart(): void {
      pressedAt = now()
    },

    /**
     * Whether a pointerup acts: not while the button is disabled, and only for the pointer
     * that went down on it, if it barely moved — capture also delivers the end of a scroll
     * or drag that merely started on the button.
     */
    pointerup(point: PressPoint, disabled: boolean): boolean {
      const start = down
      down = null
      if (disabled || !start || start.pointerId !== point.pointerId) return false
      if (
        Math.hypot(point.clientX - start.clientX, point.clientY - start.clientY) > DRAG_THRESHOLD
      ) {
        return false
      }
      pointerupActedAt = now()
      return true
    },

    /** Whether a pointer click (`detail` > 0) acts. */
    click(): boolean {
      const at = now()
      if (pointerupActedAt !== null && at - pointerupActedAt < CLICK_AFTER_POINTERUP_MS)
        return false
      return pressedAt !== null || at - mountedAt >= GHOST_CLICK_MS
    },
  }
}
